import * as React from 'react';
import { Navigate, useRouterState } from '@tanstack/react-router';

import { useHasSignedCgu } from '@/api/use-legal';
import { useAuth } from '@/auth/auth-context';

// =============================================================================
// supplier-cgu-gate.tsx — Garde d'accès CGU des écrans applicatifs (DBU §3.1 l.2432-2434).
// =============================================================================
// « Tant que la version courante des CGU n'est pas signée, l'accès aux écrans applicatifs
// (3.2 à 3.9) est strictement bloqué. Toute tentative d'accès direct par URL redirige
// automatiquement vers l'écran de signature. La session est conservée. »
//
// Défense-en-profondeur FRONT : l'ACL réelle reste API/RLS (le serveur bloque les données
// et les actions d'un contact non-signataire). Cette garde empêche la NAVIGATION UI d'un
// fournisseur non-signataire vers un écran applicatif (accès direct par URL inclus).
//
// PORTÉE : QUE le rôle 'supplier'. L'admin (et l'interim admin, P-ROLE-INTERIM) passe sans
// requête. `hasSigned` est l'AUTORITÉ API (useHasSignedCgu, calculé vs currentVersionId).
// FAIL-CLOSED : l'écran applicatif n'est rendu QUE si hasSigned === true ; pending → attente ;
// false / erreur / indéterminé → redirection vers /cgu (aligné « strictement bloqué »).

export interface SupplierCguGateProps {
  readonly children: React.ReactNode;
}

export function SupplierCguGate({
  children,
}: SupplierCguGateProps): React.JSX.Element {
  const auth = useAuth();
  const isSupplier = auth.role === 'supplier';
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  // La requête n'est émise QUE pour un fournisseur (admin/interim : aucune requête CGU).
  const signed = useHasSignedCgu({ enabled: isSupplier });

  // Les pages LÉGALES (/cgu signature-relecture, /mentions-legales) ne sont JAMAIS gatées : le gate
  // protège les ÉCRANS APPLICATIFS, pas la page de signature elle-même (sinon un fournisseur non-signataire
  // ne pourrait jamais afficher les CGU à signer). BUGFIX 2026-09-12 (chrome conservée sur ces pages).
  if (pathname === '/cgu' || pathname === '/mentions-legales') {
    return <>{children}</>;
  }

  if (!isSupplier) {
    return <>{children}</>;
  }

  if (signed.isPending) {
    return (
      <p role="status" className="text-sm text-muted-foreground">
        Vérification de la signature des CGU…
      </p>
    );
  }

  // FAIL-CLOSED : seul un fournisseur ayant signé la version courante accède aux écrans applicatifs.
  if (signed.data?.hasSigned === true) {
    return <>{children}</>;
  }

  // Non signée / erreur / indéterminé → accès bloqué, redirection vers la signature (session conservée).
  return <Navigate to="/cgu" />;
}
